import { useState } from 'react';

import type { SubmissionChannel } from '../../api';
import { Form, FormError, SelectField, SubmitButton, TextField } from '../../components';
import { CHANNEL_OPTIONS, SUBMISSION_COPY } from './submissionCycle';

/**
 * The two outcomes an analyst records against a packaged file.
 *
 * Shared by a contract's own month and by a company's single file: the rule
 * and the words are the same whichever record they land on, so the forms only
 * collect the fields and leave the request to whoever renders them.
 */

/** What the marker sends, before it knows which record it is about. */
export interface SentFields {
  readonly channel: SubmissionChannel;
  readonly reference?: string;
  readonly proofDocumentId?: string;
  readonly notes?: string;
  readonly sentAt?: string;
}

/**
 * The marker, and the rule it will not let an analyst past.
 *
 * At least one of the three forms of proof has to carry something. The check is
 * here *and* on the server, and that is not duplication with a straight face:
 * the server's is the rule — it has to hold for the partner's client too — and
 * this one exists so that an analyst who forgot the protocol number is told
 * before the request goes, next to the fields they have to fill in.
 */
export function MarkSentForm({
  onSubmit,
  pending,
  error,
  onCancel,
  allowProofDocument = true,
}: {
  onSubmit: (fields: SentFields) => void;
  pending: boolean;
  error: unknown;
  onCancel: () => void;
  /**
   * False for a group's file: a receipt document belongs to one company's
   * store, and the file to several, so the server takes a protocol or a note.
   */
  allowProofDocument?: boolean;
}) {
  const [channel, setChannel] = useState<SubmissionChannel | ''>('');
  const [reference, setReference] = useState('');
  const [proofDocumentId, setProofDocumentId] = useState('');
  const [notes, setNotes] = useState('');
  const [sentAt, setSentAt] = useState('');
  const [showProofError, setShowProofError] = useState(false);

  const hasProof = [reference, proofDocumentId, notes].some((value) => value.trim() !== '');

  return (
    <Form
      aria-label={SUBMISSION_COPY.markSent}
      onSubmit={() => {
        if (!hasProof || channel === '') {
          setShowProofError(true);
          return;
        }
        onSubmit({
          channel,
          ...(reference.trim() !== '' ? { reference: reference.trim() } : {}),
          ...(proofDocumentId.trim() !== '' ? { proofDocumentId: proofDocumentId.trim() } : {}),
          ...(notes.trim() !== '' ? { notes: notes.trim() } : {}),
          // A date input gives a calendar day; the API wants a moment. Midday
          // UTC rather than midnight so that the São Paulo day the analyst
          // picked is the São Paulo day that gets recorded.
          ...(sentAt !== '' ? { sentAt: `${sentAt}T12:00:00Z` } : {}),
        });
      }}
    >
      <FormError error={error} />
      <SelectField
        label={SUBMISSION_COPY.channelLabel}
        hint={SUBMISSION_COPY.channelHint}
        required
        value={channel}
        onChange={setChannel}
        options={CHANNEL_OPTIONS}
        placeholder="Selecione"
      />
      <TextField
        label={SUBMISSION_COPY.sentAtLabel}
        hint={SUBMISSION_COPY.sentAtHint}
        type="date"
        value={sentAt}
        onChange={setSentAt}
      />
      <TextField
        label={SUBMISSION_COPY.referenceLabel}
        hint={SUBMISSION_COPY.referenceHint}
        value={reference}
        onChange={setReference}
      />
      {allowProofDocument && (
        <TextField
          label={SUBMISSION_COPY.proofDocumentLabel}
          hint={SUBMISSION_COPY.proofDocumentHint}
          value={proofDocumentId}
          onChange={setProofDocumentId}
        />
      )}
      <TextField
        label={SUBMISSION_COPY.notesLabel}
        hint={SUBMISSION_COPY.notesHint}
        value={notes}
        onChange={setNotes}
      />
      {showProofError && !hasProof && (
        <p className="field__error" role="alert">
          {allowProofDocument ? SUBMISSION_COPY.proofRequired : SUBMISSION_COPY.groupProofRequired}
        </p>
      )}
      <div className="form__actions">
        <SubmitButton pending={pending}>{SUBMISSION_COPY.confirmSent}</SubmitButton>
        <button type="button" className="button button--ghost" onClick={onCancel}>
          {SUBMISSION_COPY.cancel}
        </button>
      </div>
    </Form>
  );
}

/** The refusal, with the reason whoever prepares the next attempt will read. */
export function MarkFailedForm({
  onSubmit,
  pending,
  error,
  onCancel,
}: {
  onSubmit: (reason: string) => void;
  pending: boolean;
  error: unknown;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState('');

  return (
    <Form aria-label={SUBMISSION_COPY.markFailed} onSubmit={() => onSubmit(reason.trim())}>
      <FormError error={error} />
      <TextField
        label={SUBMISSION_COPY.reasonLabel}
        hint={SUBMISSION_COPY.reasonHint}
        required
        value={reason}
        onChange={setReason}
      />
      <div className="form__actions">
        <button type="submit" className="button button--danger" disabled={pending}>
          {SUBMISSION_COPY.confirmFailed}
        </button>
        <button type="button" className="button button--ghost" onClick={onCancel}>
          {SUBMISSION_COPY.cancel}
        </button>
      </div>
    </Form>
  );
}
